
import * as THREE from "../../../libs/three.js/build/three.module.js";

import {t} from "../../digitization/i18n/index.js";
// Feature A (PointView spec, Sprint 2) - up to 6 simultaneous section
// planes cutting both the point cloud (via PointCloudMaterial.setClipPlanes,
// a fixed-size shader uniform array - see pointcloud.vs and the comment
// there explaining why this is a *separate* mechanism from Potree's own
// clip-box tool rather than reusing it) and every loaded IFC/OBJ model
// (via Three.js's own native per-material clippingPlanes, already enabled
// app-wide - see index.html's renderer.localClippingEnabled and the
// existing updateIfcClipping, which this extends rather than replaces).
//
// A plane is stored in *global* coordinates as a point + unit normal,
// never as a reference to the originating triangle - per the spec's own
// "Plane-definitionen gemmes i globale koordinater ... Den må ikke gemmes
// som en reference til den oprindelige trekant alene". The normal always
// points toward the side that's KEPT; "vend snit" just negates it.

const MAX_SECTION_PLANES = 6;
let planeIdSeq = 0;

// A plane whose own "Foran" (far) offset is at or beyond this distance
// (meters) is treated as an ordinary unbounded half-space cut rather than
// a deliberately narrowed slab - the default for a freshly-created plane,
// reproducing exactly the single-sided cut this feature always had before
// per-plane slice-width offsets existed. Large enough that no real
// point cloud/IFC model will ever reach it, small enough to stay a plain
// finite number everywhere (UI, serialization, the shader's clip-plane
// uniforms) instead of needing Infinity special-cased throughout.
export const SECTION_PLANE_UNBOUNDED_FAR = 1000;

export class SectionPlane{

	constructor(point, normal, name){
		this.id = `section_${++planeIdSeq}`;
		this.point = point.clone();
		this.normal = normal.clone().normalize();
		this.enabled = true;
		this.name = name || t("Snit {0}", planeIdSeq);
		// Per-plane slice width (meters), offsets along the normal from
		// `point` - "Bagved" (near, against the normal) and "Foran" (far,
		// with the normal). Defaults reproduce today's plain one-sided cut
		// (nothing kept behind the plane, everything kept in front of it)
		// until deliberately narrowed into a thin slab - from either the
		// "Aktive snit" row's own controls or the 2D section view's
		// (see index.html's buildSectionPlaneRow / updateSectionPlaneClipping) -
		// both edit these same two properties, so the width set in one
		// place is exactly what the other shows.
		this.thicknessNear = 0;
		this.thicknessFar = SECTION_PLANE_UNBOUNDED_FAR;
	}

	flip(){
		this.normal.negate();
	}

	// Moves the plane along its own normal - the manipulator, the panel's
	// slider, and Ctrl+scroll all funnel through this one method.
	moveAlongNormal(distance){
		this.point.addScaledVector(this.normal, distance);
	}

	// The plane's slice as one or two native THREE.Plane half-spaces
	// (AND'd together by the caller) - one bounding the near/"Bagved" side
	// at `point - normal*thicknessNear`, one bounding the far/"Foran" side
	// at `point + normal*thicknessFar`. With the defaults above this is
	// numerically indistinguishable, for any real scene, from the single
	// unbounded half-space cut this used to be - see the class comment.
	toThreePlanes(){
		let nearPoint = this.point.clone().addScaledVector(this.normal, -this.thicknessNear);
		let farPoint = this.point.clone().addScaledVector(this.normal, this.thicknessFar);
		return [
			new THREE.Plane().setFromNormalAndCoplanarPoint(this.normal, nearPoint),
			new THREE.Plane().setFromNormalAndCoplanarPoint(this.normal.clone().negate(), farPoint),
		];
	}

	// The elevation (Z - this app's up-axis throughout, per every other
	// module) of the plane's own defining point, shown numerically in the
	// panel per the spec's "vises numerisk som globalt punkt samt, hvor
	// relevant, kote".
	get elevation(){
		return this.point.z;
	}
}

export class SectionPlaneSet{

	constructor(){
		this.planes = [];
	}

	get maxPlanes(){
		return MAX_SECTION_PLANES;
	}

	canAddMore(){
		return this.planes.length < MAX_SECTION_PLANES;
	}

	addPlane(point, normal, name){
		if(!this.canAddMore()){
			return null;
		}
		let plane = new SectionPlane(point, normal, name);
		this.planes.push(plane);
		return plane;
	}

	removePlane(id){
		this.planes = this.planes.filter(p => p.id !== id);
	}

	getPlane(id){
		return this.planes.find(p => p.id === id) || null;
	}

	// "Behold kun dette snit" - removes every other plane.
	keepOnly(id){
		let plane = this.getPlane(id);
		this.planes = plane ? [plane] : [];
	}

	clear(){
		this.planes = [];
	}

	// Every active (enabled) plane's own one-or-two-plane slice (see
	// SectionPlane.toThreePlanes), flattened into one list - ready for
	// both IFC/OBJ's material.clippingPlanes and the point cloud's
	// setClipPlanes, which AND every plane in the list together.
	getActiveThreePlanes(){
		return this.planes.filter(p => p.enabled).flatMap(p => p.toThreePlanes());
	}

	// Plain, JSON-serializable data - no Three.js object references -
	// matching the app-wide ViewerState pattern (Feature I).
	serialize(){
		return this.planes.map(p => ({
			id: p.id,
			point: [p.point.x, p.point.y, p.point.z],
			normal: [p.normal.x, p.normal.y, p.normal.z],
			enabled: p.enabled,
			name: p.name,
			thicknessNear: p.thicknessNear,
			thicknessFar: p.thicknessFar,
		}));
	}

	restore(data){
		this.planes = (data || []).map(d => {
			let plane = new SectionPlane(
				new THREE.Vector3(d.point[0], d.point[1], d.point[2]),
				new THREE.Vector3(d.normal[0], d.normal[1], d.normal[2]),
				d.name);
			plane.id = d.id || plane.id;
			// Older saves predate per-plane slice width - fall back to the
			// same "plain unbounded cut" default a brand new plane gets.
			plane.thicknessNear = typeof d.thicknessNear === "number" ? d.thicknessNear : 0;
			plane.thicknessFar = typeof d.thicknessFar === "number" ? d.thicknessFar : SECTION_PLANE_UNBOUNDED_FAR;
			plane.enabled = d.enabled !== false;
			return plane;
		});
	}
}
